import React, { useState, useEffect } from 'react';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import { toast } from 'react-toastify';
import moment from 'moment';
import Flatpickr from 'react-flatpickr';
import { useAuth } from "../../Common/Configurations/AuthContext";
import { RiArrowLeftLine } from 'react-icons/ri';
import "./PackageManageApplicants.css"

const DEFAULT_PROFILE = "/assets/images/avatar.png";
const DEFAULT_DOCUMENT = "/assets/images/doc_bgc.png";

const initialFieldValues = {
    packageApplicantId: "00000000-0000-0000-0000-000000000000",
    firstName: '',
    lastName: '',
    title: 'Mr',
    gender: 'Male',
    dateOfBirth: new Date(),
    nationality: 'Indian',
    contactEmail: '',
    contactPhoneNumber: '',
    passportNumber: '',
    passportCountry: 'India',
    passportIssueDate: new Date(),
    passportExpireDate: new Date(),
    travelerType: 'Adult',
    packageApplicantStatus: 'Submitted',
    profileFile: null,
    passportFrontFile: null,
    passportBackFile: null,
    additionalDocumentFiles: [],
};

const PackageApplicantForm = ({ packageId, applicantForEdit, parentApplicantId, onSuccess, onCancel }) => {
    const { user } = useAuth();
    const [values, setValues] = useState(initialFieldValues);
    const [recordForEdit, setRecordForEdit] = useState(null);
    const [previews, setPreviews] = useState({
        profileSrc: DEFAULT_PROFILE,
        passportFrontSrc: DEFAULT_DOCUMENT,
        passportBackSrc: DEFAULT_DOCUMENT,
    });
    const [btnLoading, setBtnLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setRecordForEdit(applicantForEdit); 
        if (applicantForEdit) {
            setValues({
                ...initialFieldValues,
                ...applicantForEdit,
                dateOfBirth: moment(applicantForEdit.dateOfBirth).toDate(),
                passportIssueDate: moment(applicantForEdit.passportIssueDate).toDate(),
                passportExpireDate: moment(applicantForEdit.passportExpireDate).toDate(),
                profileFile: null,
                passportFrontFile: null,
                passportBackFile: null,
                additionalDocumentFiles: [],
            });
            setPreviews({
                profileSrc: applicantForEdit.profileSrc || DEFAULT_PROFILE,
                passportFrontSrc: applicantForEdit.passportFrontSrc || DEFAULT_DOCUMENT,
                passportBackSrc: applicantForEdit.passportBackSrc || DEFAULT_DOCUMENT,
            });
        } else {
            resetForm(); 
        }
    }, [applicantForEdit]);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };
    
    const handleFileChange = (e, fileField, previewField) => {
        const file = e.target.files[0];
        if (file) {
            setValues({ ...values, [fileField]: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviews(prev => ({ ...prev, [previewField]: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };
    
    const handleAdditionalFilesChange = (e) => {
        setValues({ ...values, additionalDocumentFiles: [...e.target.files] });
    };

    const validate = () => {
        let temp = {};
        temp.firstName = values.firstName.trim() !== "";
        temp.lastName = values.lastName.trim() !== "";
        temp.passportNumber = values.passportNumber.trim() !== "";
        
        // Conditional validation: only for the main applicant
        if (!parentApplicantId) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            temp.contactEmail = emailRegex.test(values.contactEmail);
            temp.contactPhoneNumber = values.contactPhoneNumber.trim() !== "";
        }
        
        setErrors(temp);
        return Object.values(temp).every((x) => x === true);
    };


    const resetForm = () => {
        setValues(initialFieldValues);
        setPreviews({
            profileSrc: DEFAULT_PROFILE,
            passportFrontSrc: DEFAULT_DOCUMENT,
            passportBackSrc: DEFAULT_DOCUMENT,
        });
        setBtnLoading(false);
        setRecordForEdit(null);
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnLoading(true);
            
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                if (key.toLowerCase().includes('date')) {
                    formData.append(key, moment(value).format('YYYY-MM-DD'));
                } else if (!key.toLowerCase().includes('file') && value !== null) {
                    formData.append(key, value);
                }
            });

            if (values.profileFile) formData.append('profileFile', values.profileFile);
            if (values.passportFrontFile) formData.append('passportFrontFile', values.passportFrontFile);
            if (values.passportBackFile) formData.append('passportBackFile', values.passportBackFile);
            if (values.additionalDocumentFiles.length > 0) {
                values.additionalDocumentFiles.forEach(file => {
                    formData.append('additionalDocumentFiles', file);
                });
            }

            formData.append('packageId', packageId);
            formData.append('userId', user?.id);
            if (parentApplicantId) formData.append('parentApplicantId', parentApplicantId);
            
            try {
                const isNew = values.packageApplicantId === "00000000-0000-0000-0000-000000000000";

                if (isNew) {
                    await apiService.create(config.CREATEAPPLICANT, formData);
                } else {
                    await apiService.update(`${config.UPDATEAPPLICANT}/${values.packageApplicantId}`, formData);
                }
                
                onSuccess(); 
                
            } catch (error) {
                console.error("Error submitting form", error);
            } finally {
                setBtnLoading(false);
            }
        }
    };

    const applyErrorClass = (field) =>
        field in errors && errors[field] === false ? "is-invalid" : "";

    return (
        <div className="p-2" style={{ backgroundColor: '#ece5e5' }}>
             <div className="applicant-form-header">
                <button type="button" className="btn btn-primary" onClick={onCancel}>
                    <RiArrowLeftLine className="me-1" /> Back to List
                </button>
            </div>
            <h4 className="mb-4">{recordForEdit ? 'Edit Applicant' : 'Add New Applicant'}</h4>
            <form onSubmit={handleSubmit} noValidate>
                <div className="card mb-4">
                    <div className="card-body">
                       
                        <div className="row text-center">
                            <div className="col-md-4">
                                <label className="form-label">Profile Photo</label>
                                <img src={previews.profileSrc} alt="Profile" className="img-thumbnail mb-2" style={{height: '150px'}} />
                                <input type="file" className="form-control form-control-sm" accept="image/*" onChange={e => handleFileChange(e, 'profileFile', 'profileSrc')} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Passport Front</label>
                                <img src={previews.passportFrontSrc} alt="Passport Front" className="img-thumbnail mb-2" style={{height: '150px'}} />
                                <input type="file" className="form-control form-control-sm" accept="image/*" onChange={e => handleFileChange(e, 'passportFrontFile', 'passportFrontSrc')} />
                            </div>
                             <div className="col-md-4">
                                <label className="form-label">Passport Back</label>
                                <img src={previews.passportBackSrc} alt="Passport Back" className="img-thumbnail mb-2" style={{height: '150px'}} />
                                <input type="file" className="form-control form-control-sm" accept="image/*" onChange={e => handleFileChange(e, 'passportBackFile', 'passportBackSrc')} />
                            </div>
                        </div>
                    </div>
                </div>

                 <div className="card">
                     <div className="card-body">
                        <h5 className="card-title">Personal & Passport Details</h5>
                         <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label">First Name *</label>
                                <input type="text" name="firstName" value={values.firstName} onChange={handleInputChange} className={`form-control ${applyErrorClass('firstName')}`} />
                            </div>
                             <div className="col-md-4 mb-3">
                                <label className="form-label">Last Name *</label>
                                <input type="text" name="lastName" value={values.lastName} onChange={handleInputChange} className={`form-control ${applyErrorClass('lastName')}`} />
                            </div>
                             <div className="col-md-4 mb-3">
                                <label className="form-label">Passport Number *</label>
                                <input type="text" name="passportNumber" value={values.passportNumber} onChange={handleInputChange} className={`form-control ${applyErrorClass('passportNumber')}`} />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Date of Birth</label>
                                <Flatpickr value={values.dateOfBirth} className="form-control" options={{ dateFormat: "d-M-Y" }} onChange={([date]) => setValues({ ...values, dateOfBirth: date })} />
                            </div>
                             <div className="col-md-4 mb-3">
                                <label className="form-label">Passport Issue Date</label>
                                <Flatpickr value={values.passportIssueDate} className="form-control" options={{ dateFormat: "d-M-Y" }} onChange={([date]) => setValues({ ...values, passportIssueDate: date })} />
                            </div>
                              <div className="col-md-4 mb-3">
                                <label className="form-label">Passport Expire Date</label>
                                <Flatpickr value={values.passportExpireDate} className="form-control" options={{ dateFormat: "d-M-Y" }} onChange={([date]) => setValues({ ...values, passportExpireDate: date })} />
                            </div>
                             <div className="col-md-4 mb-3">
                                <label className="form-label">Traveler Type</label>
                                <select name="travelerType" value={values.travelerType} onChange={handleInputChange} className="form-select">
                                    <option value="Adult">Adult</option>
                                    <option value="Child">Child</option>
                                    <option value="Infant">Infant</option>
                                </select>
                            </div>
                             <div className="col-md-4 mb-3">
                                <label className="form-label">
                                    Email
                                    {!parentApplicantId && ' *'} {/* Show asterisk if it's a main applicant */}
                                </label>
                                <input type="email" name="contactEmail" value={values.contactEmail} onChange={handleInputChange} className={`form-control ${applyErrorClass('contactEmail')}`} />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">
                                    Phone Number
                                    {!parentApplicantId && ' *'} {/* Show asterisk if it's a main applicant */}
                                </label>
                                <input type="tel" name="contactPhoneNumber" value={values.contactPhoneNumber} onChange={handleInputChange} className={`form-control ${applyErrorClass('contactPhoneNumber')}`} />
                            </div>
                            <div className="col-12 mb-3">
                                <label className="form-label">Additional Documents</label>
                                <input type="file" className="form-control" multiple onChange={handleAdditionalFilesChange} />
                            </div>
                         </div>
                         <div className="text-end">
                             <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>Cancel</button>
                             <button type="submit" className="btn btn-primary" disabled={btnLoading}>
                                 {btnLoading ? 'Saving...' : 'Save Applicant'}
                             </button>
                         </div>
                     </div>
                 </div>
            </form>
        </div>
    );
};

export default PackageApplicantForm;